// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./DeliveryDetails.scss";

import { AuthContext } from '../../context/AuthContext';
import { firestore, storage } from "../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot, setDoc, serverTimestamp, deleteField } from "firebase/firestore";
import { format } from "date-fns";

import PropertyField from "../PropertyField/PropertyField";
import BaleView from "../BaleView/BaleView";
import DeliveryImageView from "../DeliveryImageView/DeliveryImageView";
import ImageSquare from "../../shared_components/ImageSquare/ImageSquare";
import DocumentScanner from "../DocumentScanner/DocumentScanner";
import CreateClaimButton from "../CreateClaimButton/CreateClaimButton";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { ClaimImageView, ClaimReportButton } from "../ClaimModals/ClaimModals";
import { uploadFileToFirebaseStorage } from "../../shared_components/utils/ImgUtils";
import { getClaimSettings } from "../../shared_components/utils/FirebaseUtils";

// Importing icons
import material_icon from '../../assets/icons/lightblue/piechart.png';
import supplier_icon from '../../assets/icons/lightblue/delivery.png';
import document_icon from '../../assets/icons/lightblue/document.png';
import user_icon from '../../assets/icons/lightblue/user.png';
import date_icon from '../../assets/icons/lightblue/calendar.png';
import notes_icon from '../../assets/icons/lightblue/document.png';
import bales_icon from '../../assets/icons/lightblue/bales_stacked.png';
import location_icon from '../../assets/icons/lightblue/location.png';
import shapes_icon from '../../assets/icons/lightblue/shape.png';
import piechart_icon from '../../assets/icons/lightblue/piechart.png';
import color_icon from '../../assets/icons/lightblue/color.png';
import alert_icon from '../../assets/icons/lightblue/alert.png';
import alert_icon_red from '../../assets/icons/alert.png';
import notes_icon_white from '../../assets/icons/white/document.png';
import label_icon_green from '../../assets/icons/white/label.png';
import checkmark_icon from '../../assets/icons/checkmark.png';
import drafting_icon from '../../assets/icons/lightblue/notes.png';

const iconMapping = {
    "notes": document_icon,
    "user": user_icon,
    "label": document_icon,
    "location": location_icon,
    "bales": bales_icon,
    "shapes": shapes_icon,
    "piechart": piechart_icon,
    "color": color_icon,
}

export default function DeliveryDetails({ deliveryId, deliveriesSettings }) {
    const { i18n, t } = useTranslation();
    const { currentUser, labelsets, logEventCustom, suppliers } = React.useContext(AuthContext);
    const [delivery, setDelivery] = React.useState({});
    const [showBaleId, setShowBaleId] = React.useState(null);
    const [showDeliveryImageId, setShowDeliveryImageId] = React.useState(null);
    const [showDocumentsPopup, setShowDocumentsPopup] = React.useState(false);
    const [showClaimImageId, setShowClaimImageId] = React.useState(null);
    const [showConfirmClaim, setShowConfirmDeleteClaim] = React.useState(false);
    const [claimSettings, setClaimSettings] = React.useState({});
    const unsubscribeDelivery = React.useRef(null);

    // Load claim settings
    React.useEffect(() => {
        getClaimSettings(firestore, currentUser.company.company).then((settings) => {
            setClaimSettings(settings);
        });
    }, []);

    // Subscribe to active delivery and load data
    React.useEffect(() => {
        // Unsubscribe from previous query
        if (unsubscribeDelivery.current) unsubscribeDelivery.current();

        if (!deliveryId) return;

        // Subscribe to active delivery
        const deliveryDocRef = doc(firestore, "clients", currentUser.company.company, "deliveries", deliveryId);
        const unsubscribeNew = onSnapshot(deliveryDocRef, (snapshot) => {
            if (!snapshot.exists || !snapshot.data()) return;
            setDelivery({ ...snapshot.data(), date: snapshot.data().date.toDate() });
        });
        unsubscribeDelivery.current = () => unsubscribeNew();

        return () => {
            if (unsubscribeDelivery.current) {
                unsubscribeDelivery.current();
            }
        }
    }, [deliveryId, currentUser.company.company]);

    function handleDeliveryFieldChange(event) {
        /** Change value of a delivery field and update on database */
        const { name, value } = event.target;
        const deliveryRef = doc(firestore, "clients", currentUser.company.company, "deliveries", deliveryId);
        setDoc(deliveryRef, { [name]: value }, { merge: true });

        // Log the event to analytics
        logEventCustom("edit_field", { section: "delivery", field: name });
    }

    function getPreviousId(collection, currentId) {
        /** Get the id of the previous bale */
        const Ids = Object.keys(collection).sort();
        const currentIndex = Ids.indexOf(currentId);
        if (currentIndex === 0 || currentIndex === -1) return currentId;
        return Ids[currentIndex - 1];
    }

    function getNextId(collection, currentBaleId) {
        /** Get the id of the next bale */
        const baleIds = Object.keys(collection).sort();
        const currentBaleIndex = baleIds.indexOf(currentBaleId);
        if (currentBaleIndex === baleIds.length - 1 || currentBaleIndex === -1) return currentBaleId;
        return baleIds[currentBaleIndex + 1];
    }

    function getSortedBaleEntries() {
        /* Sort bales by flagged status and then by id */
        if (!delivery?.bales) return [];

        return Object.entries(delivery?.bales).sort((a, b) => {
            return a[1]?.flagged && !b[1]?.flagged ? -1 :
                !a[1]?.flagged && b[1]?.flagged ? 1 : a[0].localeCompare(b[0]);
        });
    }

    function getPreviousBaleId(currentBaleId) {
        /** Get the id of the previous bale */
        const baleIds = getSortedBaleEntries().map(([baleId, bale]) => baleId);
        const currentBaleIndex = baleIds.indexOf(currentBaleId);
        if (currentBaleIndex === 0 || currentBaleIndex === -1) return currentBaleId;
        return baleIds[currentBaleIndex - 1];
    }

    function getNextBaleId(currentBaleId) {
        /** Get the id of the next bale */
        const baleIds = getSortedBaleEntries().map(([baleId, bale]) => baleId);
        const currentBaleIndex = baleIds.indexOf(currentBaleId);
        if (currentBaleIndex === baleIds.length - 1 || currentBaleIndex === -1) return currentBaleId;
        return baleIds[currentBaleIndex + 1];
    }

    async function handleClaimImageUpload(e) {
        /** Upload a claim image to the delivery from local filesystem */
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (event) => {
            const file = event.target.files[0];
            if (!file) return;

            // Upload the image to firebase storage
            const storagePath = `clients/${currentUser.company.company}/deliveries/reclamations/${deliveryId}/${file.name}`
            const image_URL = await uploadFileToFirebaseStorage(storage, file, storagePath);

            // Add the image url to the claim data
            const imageId = `${currentUser.uid}_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}`;
            handleDeliveryFieldChange({
                target: {
                    name: "claim_data", value: {
                        claim_images: {
                            [imageId]: {
                                image_URL: image_URL,
                                timestamp: serverTimestamp(),
                                uploadedFromDashboard: true,
                            }
                        }
                    }
                }
            });
        };

        // Trigger the file dialog
        input.click();

        // Log the event to analytics
        logEventCustom("upload_claim_image", { deliveryId: deliveryId });
    }

    async function removeClaim() {
        /** Remove the claim from the delivery */
        const deliveryRef = doc(firestore, "clients", currentUser.company.company, "deliveries", deliveryId);
        setDoc(deliveryRef, { claim_data: deleteField() }, { merge: true });

        // Log the event to analytics
        logEventCustom("delete_claim", { deliveryId: deliveryId });
    }

    const materialOptions = React.useMemo(() => {
        const newMaterialOptions = {};
        for (const [id, m] of Object.entries(labelsets['materials']['labels'])) {
            newMaterialOptions[id] = m.display_name;
        }
        return newMaterialOptions;
    }, [labelsets]);

    const supplierOptions = React.useMemo(() => {
        const newSupplierOptions = {};
        Object.entries(suppliers).forEach(([key, label]) => {
            if (label?.supplier_code) {
                newSupplierOptions[key] = `${label.supplier_code} ${label.display_name}`;
            } else {
                newSupplierOptions[key] = label.display_name;
            }
        });
        return newSupplierOptions;
    }, [suppliers]);

    const stockFieldOptions = React.useMemo(() => {
        const newStockFieldOptions = {};
        Object.entries(labelsets?.stock_field?.labels ?? {}).forEach(([key, label]) => {
            newStockFieldOptions[key] = label.display_name;
        });
        return newStockFieldOptions;
    }, [labelsets]);

    // Render loading screen if delivery is not loaded
    if (!delivery || Object.keys(delivery).length === 0) {
        return (
            <div className="delivery-details">
                <p>Loading delivery details...</p>
            </div>
        )
    }

    return (
        <div className="delivery-details">
            <div className="delivery-properties-section">
                <div className="delivery-properties">
                    <PropertyField
                        icon={material_icon}
                        title={t("property_material").toUpperCase()}
                        value={delivery?.base_labels?.materials}
                        options={materialOptions}
                        name={"base_labels.materials"}
                        type="select"
                        handleChange={(e) => {handleDeliveryFieldChange({target: {name: "base_labels", value: {"materials": e.target.value}}})}}
                        editable={true}
                        grid_span={1}
                    />
                    <PropertyField
                        icon={supplier_icon}
                        title={t("property_supplier").toUpperCase()}
                        value={delivery.supplier_id}
                        name={"supplier_id"}
                        type="select"
                        options={supplierOptions}
                        handleChange={handleDeliveryFieldChange}
                        editable={true}
                        grid_span={2}
                    />
                    <PropertyField
                        icon={document_icon}
                        title={t("property_document_scanner").toUpperCase()}
                        value={
                            Object.keys(delivery?.documents_data?.scanned_documents ?? {}).length > 0 ?
                                `${Object.keys(delivery?.documents_data?.scanned_documents ?? {}).length} ${t("delivery_documents_scanned")}` :
                                t("delivery_no_documents_scanned")
                        }
                        name={"slip_nr"}
                        type={null}
                        handleChange={handleDeliveryFieldChange}
                        editable={false}
                        onClick={() => {
                            if (Object.keys(delivery?.documents_data?.scanned_documents ?? {}).length > 0) {
                                setShowDocumentsPopup(true);
                            }
                        }}
                    />
                    <PropertyField
                        icon={location_icon}
                        title="LAGER FELD"
                        value={delivery.storage_field}
                        name={"storage_field"}
                        type="select"
                        options={stockFieldOptions}
                        handleChange={handleDeliveryFieldChange}
                        editable={true}
                    />
                    {Object.entries(deliveriesSettings?.custom_delivery_fields ?? {}).map(([key, value]) => {
                        return (
                            <PropertyField
                                icon={iconMapping[value.icon]}
                                title={value.title}
                                value={delivery?.[key]}
                                name={key}
                                type={value.type}
                                handleChange={handleDeliveryFieldChange}
                                editable={true}
                            />
                        )
                    })}
                    <PropertyField
                        icon={user_icon}
                        title={t("property_submitted_by").toUpperCase()}
                        value={delivery.delivery_submitted_by_user}
                        name={"delivery_submitted_by_user"}
                        type="select"
                        options={deliveriesSettings?.delivery_submitted_by_user ?? {}}
                        handleChange={handleDeliveryFieldChange}
                        editable={true}
                    />
                    <PropertyField 
                        icon={date_icon} 
                        title={t("property_date").toUpperCase()} 
                        value={delivery.date} 
                        name="date"
                        type="datetime-local"
                        handleChange={handleDeliveryFieldChange}
                        editable={true}
                    />
                </div>
                <div className="delivery-properties">
                    <PropertyField 
                        icon={notes_icon} 
                        title={t("property_notes").toUpperCase()} 
                        value={delivery.notes} 
                        name="notes"
                        type="textarea"
                        handleChange={handleDeliveryFieldChange}
                        editable={true}
                        grid_span={3}
                    />
                </div>
            </div>

            {/* Claims Section */}
            {['confirmed', 'editing_dashboard'].includes(delivery?.claim_data?.claim_status) ?
                <div className="delivery-claim">
                    {/* Header for Confirmed claims */}
                    {delivery?.claim_data?.claim_status === "confirmed" &&
                        <div className="delivery-claim-header">
                            <img className="alert-icon" src={alert_icon_red} />
                            <p className="claim-title">{`${t("delivery_claim_title")} #${delivery.claim_data?.claim_nr?.toString().padStart(4, '0')}`}</p>
                            {delivery?.claim_data?.created_at &&
                                <p className="claim-user-time">
                                    {`erfasst von ${delivery?.claim_data?.created_by_displayname} am ${format(delivery?.claim_data?.created_at.toDate(), "dd.MM.yyyy HH:mm")}`}
                                </p>}
                            <button
                                className="delete-claim-button"
                                onClick={() => { setShowConfirmDeleteClaim(true) }}
                            >
                                {t("delivery_delete_claim")}
                            </button>
                            <ConfirmationModal
                                title={t("delivery_delete_claim_confirm")}
                                content={
                                    <div>
                                        <p style={{ margin: "0" }}>{`${t("delivery_claim_title")} #${delivery.claim_data?.claim_nr?.toString().padStart(4, '0')}`}</p>
                                        {delivery.claim_data?.created_by_displayname &&
                                            <p style={{ margin: "0" }}>{`${t("delivery_delete_claim_user")}: ${delivery.claim_data?.created_by_displayname}`}</p>}
                                        {delivery.claim_data?.created_at &&
                                            <p style={{ margin: "0" }}>{`${t("delivery_delete_claim_date")}: ${format(delivery?.claim_data?.created_at?.toDate(), "dd.MM.yyyy HH:mm")}`}</p>}
                                    </div>
                                }
                                show={showConfirmClaim}
                                setShow={setShowConfirmDeleteClaim}
                                onConfirm={removeClaim}
                            />
                        </div>}

                    {/* Header for new claims */}
                    {delivery?.claim_data?.claim_status === "editing_dashboard" &&
                        <div className="delivery-claim-header">
                            <img className="alert-icon" src={drafting_icon} />
                            <p className="claim-title">{t("delivery_new_claim_title")}</p>
                            <button
                                className="delete-claim-button"
                                onClick={() => { setShowConfirmDeleteClaim(true) }}
                            >
                                {t("delivery_discard_claim")}
                            </button>
                            <button
                                className="save-claim-button"
                                onClick={() => { handleDeliveryFieldChange({ target: { name: "claim_data", value: { claim_status: "confirmed" } } }) }}
                            >
                                {t("delivery_confirm_claim")}
                            </button>
                            <ConfirmationModal
                                title={t("delivery_discard_claim_draft_confirm")}
                                show={showConfirmClaim}
                                setShow={setShowConfirmDeleteClaim}
                                onConfirm={removeClaim}
                            />
                        </div>}

                    <div className="delivery-images-gallery claims-images-gallery">
                        <p className="delivery-images-title">{t("delivery_claim_images")}</p>
                        <div className="delivery-images-wrapper">
                            <div className="delivery--delivery-images-card-new" onClick={handleClaimImageUpload}>
                                <p>+</p>
                            </div>
                            {delivery?.claim_data?.claim_images ?
                                Object.entries(delivery?.claim_data?.claim_images)
                                    .sort()
                                    .map(([claimImageId, claimImage]) => {
                                        return (
                                            <div key={claimImageId} onClick={() => {
                                                setShowClaimImageId(claimImageId);
                                                logEventCustom("select_content", { content_type: "claim_image", content_id: claimImageId });
                                            }}>
                                                <ImageSquare key={claimImageId}
                                                    storage={storage}
                                                    imageUrl={claimImage.image_URL}
                                                    icons={claimImage?.notes ? [notes_icon_white] : []}
                                                />
                                            </div>
                                        )
                                    })
                                : <p className="delivery-images-no-images">{t("delivery_no_images")}</p>}
                        </div>
                    </div>
                    <div className="claim-properties">
                        <PropertyField
                            icon={alert_icon}
                            title="PROBLEMPUNKTE"
                            value={delivery?.claim_data?.delivery_issues ?? []}
                            name="delivery_issues"
                            type="multiple-choice-select"
                            handleChange={(e) => {
                                handleDeliveryFieldChange({
                                    ...e,
                                    target: { "name": "claim_data", "value": { [e.target.name]: e.target.value } }
                                })
                            }}
                            editable={true}
                            options={Object.entries(claimSettings?.delivery_issues ?? {})
                                .sort(([keyA, objA], [keyB, objB]) => {
                                    const sortIdA = objA.sort_id ?? 0;
                                    const sortIdB = objB.sort_id ?? 0;
                                    if (sortIdA !== sortIdB) {
                                        return sortIdA - sortIdB;
                                    }
                                    return objA.display_name.localeCompare(objB.display_name);
                                })
                                .reduce((acc, [id, obj]) => {
                                    acc[id] = obj.display_name;
                                    return acc;
                                }, {})
                            }
                        />
                        {delivery?.claim_data?.claim_status === "confirmed" &&
                            <ClaimReportButton
                                delivery={delivery}
                            />}
                    </div>
                </div>
                :
                <div className="delivery-no-claim">
                    <img className="checkmark-icon" src={checkmark_icon} />
                    <p className="delivery-no-claim-text">{t("delivery_no_claim")}</p>
                    <CreateClaimButton delivery={delivery} />
                </div>}

            {/* Delivery Images */}
            <div className="delivery-images-gallery">
                <p className="delivery-images-title">{t("delivery_registered_images")}</p>
                <div className="delivery-images-wrapper">
                    {delivery?.delivery_images ? Object.entries(delivery?.delivery_images).sort().map(([deliveryImageId, deliveryImage]) => {
                        return (
                            <div key={deliveryImageId} onClick={() => {
                                setShowDeliveryImageId(deliveryImageId);
                                logEventCustom("select_content", { content_type: "delivery_image", content_id: deliveryImageId });
                            }}>
                                <ImageSquare key={deliveryImageId}
                                    storage={storage}
                                    imageUrl={deliveryImage.image_URL}
                                    icons={deliveryImage?.flagged ? [alert_icon_red] : []}
                                />
                            </div>
                        )
                    })
                        : <p className="delivery-images-no-images">{t("delivery_no_images")}</p>}
                </div>
            </div>

            {/* Bale Images */}
            <div className="delivery-images-gallery">
                <p className="delivery-images-title">{t("delivery_registered_bales")}</p>
                <div className="bale-images-wrapper">
                    {delivery?.bales && getSortedBaleEntries().map(([baleId, bale]) => {
                        const icons = [];
                        if (bale?.labelled) icons.push(label_icon_green);
                        if (bale?.flagged) icons.push(alert_icon_red);
                        return (
                            <div key={baleId} onClick={() => {
                                setShowBaleId(baleId);
                                logEventCustom("select_content", { content_type: "bale", content_id: baleId });
                            }}>
                                <ImageSquare key={baleId}
                                    storage={storage}
                                    imageUrl={bale.image_URL}
                                    icons={icons}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Bale Modal */}
            {showBaleId && <BaleView
                deliveryId={deliveryId}
                baleId={showBaleId}
                deliveryImages={delivery?.delivery_images}
                onClose={() => setShowBaleId(null)}
                goToPreviousBale={() => { setShowBaleId(getPreviousBaleId(showBaleId)) }}
                goToNextBale={() => { setShowBaleId(getNextBaleId(showBaleId)) }}
                showDeliveryImage={(deliveryImageId) => { setShowBaleId(null); setShowDeliveryImageId(deliveryImageId) }}
            />}

            {/* Delivery Image Modal */}
            {showDeliveryImageId && <DeliveryImageView
                deliveryId={deliveryId}
                deliveryImageId={showDeliveryImageId}
                onClose={() => setShowDeliveryImageId(null)}
                goToPreviousDeliveryImage={() => { setShowDeliveryImageId(getPreviousId(delivery.delivery_images, showDeliveryImageId)) }}
                goToNextDeliveryImage={() => { setShowDeliveryImageId(getNextId(delivery.delivery_images, showDeliveryImageId)) }}
                showBale={(baleId) => { setShowDeliveryImageId(null); setShowBaleId(baleId) }}
            />}

            {/* Document Scanner Popup Modal */}
            <DocumentScanner
                show={showDocumentsPopup}
                onHide={() => setShowDocumentsPopup(false)}
                documentsData={delivery?.documents_data}
                deliveryId={deliveryId}
                handleDocumentsDataFieldChange={(data) => {
                    handleDeliveryFieldChange({
                        target: { "name": "documents_data", "value": data }
                    })
                }}
            />

            {/* claim Image Modal */}
            {showClaimImageId && <ClaimImageView
                delivery={delivery}
                claimImageId={showClaimImageId}
                setClaimImageId={setShowClaimImageId}
                handleClaimFieldChange={(e) => {
                    handleDeliveryFieldChange({
                        ...e,
                        target: { "name": "claim_data", "value": { "claim_images": { [e.target.name]: e.target.value } } }
                    })
                }}
            />}
        </div>
    );
} 